import MenuIcon from "@mui/icons-material/Menu";
import { AppBar, Box, Button, Container, Drawer, IconButton, Link, List, ListItemButton, ListItemText, Stack, Toolbar, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useState, type ReactNode } from "react";
import { Link as RouterLink, Outlet, useLocation } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider";
import { CookieBanner } from "../website_legal_pages/CookieBanner";

const NAV = [
  { label: "Product", to: "/features" }, { label: "Industries", to: "/industries" }, { label: "Pricing", to: "/pricing" },
  { label: "Docs", to: "/docs" }, { label: "Changelog", to: "/changelog" },
];

export function Seo({ title, description }: { title: string; description?: string }) {
  useEffect(() => {
    document.title = `${title} · CloudForge ERM`;
    const set = (sel: string, attr: string, value: string) => {
      let el = document.head.querySelector<HTMLMetaElement>(sel);
      if (!el) { el = document.createElement("meta"); const [k, v] = sel.replace("meta[", "").replace("]", "").split("="); el.setAttribute(k, v.replace(/"/g, "")); document.head.appendChild(el); }
      el.setAttribute(attr, value);
    };
    if (description) { set('meta[name="description"]', "content", description); set('meta[property="og:description"]', "content", description); }
    set('meta[property="og:title"]', "content", title);
    set('meta[property="og:type"]', "content", "website");
  }, [title, description]);
  return null;
}

export function Section({ children, alt, id }: { children: ReactNode; alt?: boolean; id?: string }) {
  return <Box id={id} component="section" sx={{ py: { xs: 6, md: 10 }, bgcolor: alt ? "background.paper" : "transparent" }}><Container maxWidth="lg">{children}</Container></Box>;
}

export function MarketingLayout() {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = useState(false);
  const { me } = useAuth();
  const location = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <AppBar position="sticky" color="inherit" elevation={0} sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Container maxWidth="lg"><Toolbar disableGutters sx={{ gap: 2 }}>
          <Typography variant="h6" component={RouterLink} to="/" color="primary" sx={{ textDecoration: "none", fontWeight: 700 }}>CloudForge ERM</Typography>
          {!mobile && <Stack direction="row" spacing={1} sx={{ ml: 3 }}>{NAV.map((n) => <Button key={n.to} component={RouterLink} to={n.to} color="inherit">{n.label}</Button>)}</Stack>}
          <Box sx={{ flexGrow: 1 }} />
          {!mobile && (me ? <Button variant="contained" component={RouterLink} to="/app">Open app</Button> : (
            <><Button component={RouterLink} to="/login" color="inherit">Sign in</Button><Button variant="contained" component={RouterLink} to="/signup">Start free trial</Button></>
          ))}
          {mobile && <IconButton onClick={() => setOpen(true)} aria-label="Open menu"><MenuIcon /></IconButton>}
        </Toolbar></Container>
      </AppBar>
      <Drawer open={open} onClose={() => setOpen(false)} anchor="right">
        <List sx={{ width: 240 }}>
          {NAV.map((n) => <ListItemButton key={n.to} component={RouterLink} to={n.to} onClick={() => setOpen(false)}><ListItemText primary={n.label} /></ListItemButton>)}
          <ListItemButton component={RouterLink} to="/login" onClick={() => setOpen(false)}><ListItemText primary="Sign in" /></ListItemButton>
          <ListItemButton component={RouterLink} to="/signup" onClick={() => setOpen(false)}><ListItemText primary="Start free trial" /></ListItemButton>
        </List>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1 }}><Outlet /></Box>
      <Box component="footer" sx={{ borderTop: 1, borderColor: "divider", py: 4, bgcolor: "background.paper" }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: "column", md: "row" }} spacing={4} justifyContent="space-between">
            <Box><Typography variant="subtitle2">CloudForge ERM</Typography><Typography variant="body2" color="text.secondary">ERP for industrial manufacturers.</Typography></Box>
            <Stack direction="row" spacing={4}>
              <Stack spacing={0.5}><Typography variant="caption" color="text.secondary">Product</Typography>{NAV.map((n) => <Link key={n.to} component={RouterLink} to={n.to} variant="body2" underline="hover">{n.label}</Link>)}</Stack>
              <Stack spacing={0.5}><Typography variant="caption" color="text.secondary">Company</Typography><Link component={RouterLink} to="/contact" variant="body2" underline="hover">Contact</Link><Link component={RouterLink} to="/demo" variant="body2" underline="hover">Book a demo</Link><Link href="https://status.cloudforge.local" variant="body2" underline="hover" target="_blank" rel="noopener">Status</Link></Stack>
              <Stack spacing={0.5}><Typography variant="caption" color="text.secondary">Legal</Typography>{["terms", "privacy", "dpa", "security", "cookies"].map((k) => <Link key={k} component={RouterLink} to={`/${k}`} variant="body2" underline="hover" sx={{ textTransform: "capitalize" }}>{k === "dpa" ? "DPA" : k}</Link>)}</Stack>
            </Stack>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 3 }}>© {new Date().getFullYear()} CloudForge. All rights reserved.</Typography>
        </Container>
      </Box>
      <CookieBanner />
    </Box>
  );
}
